/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import React from 'react';
import { createStyles } from '@/utils/styles';
import {
    Bar,
    FlexBox,
    FlexBoxAlignItems,
    FlexBoxDirection,
    FlexBoxJustifyContent,
    FlexBoxWrap,
    Icon,
    Label,
    Link,
    List,
    ListItemCustom,
    Modals,
    NotificationListItem,
    Title,
} from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useTranslations } from '@/hooks/useTranslations';
import { formatText, formatDate, mapUIStatus } from '@/utils/utils';
import { LaunchSummaryDto } from '@/commons/dtos';
import PersonalizationStatusIcon, { mapJobStatusToPersonalizationStatus } from '@/components/PersonalizationStatusIcon';
import { uiFullWidth } from '@/Styling/Theme/Theme';

interface StepProgressProps {
    name: LaunchSummaryDto['steps'][number]['name'];
    status: LaunchSummaryDto['steps'][number]['status'];
    activity: LaunchSummaryDto['steps'][number]['activity'];
}

enum MonitorLayoutStep {
    BaseTemplate = 'MONITOR_LAYOUT_BASE_PRACTICE_TEMPLATE_STEP',
    ConfigParameter = 'MONITOR_LAYOUT_CONFIGURATION_PARAMETER_STEP',
    UserCreation = 'MONITOR_LAYOUT_USER_CREATION_STEP',
    CommodityCode = 'MONITOR_LAYOUT_COMMODITY_CODE_STEP',
    PrerequisiteSetup = 'MONITOR_LAYOUT_PREREQUISITE_SETUP_STEP',
}

const StepProgress: React.FC<StepProgressProps> = ({ name, status, activity }) => {
    const { t } = useTranslations();

    return (
        <FlexBox style={styles.contentWrapper} direction={FlexBoxDirection.Column} wrap={FlexBoxWrap.Wrap} alignItems={FlexBoxAlignItems.Start}>
            <ListItemCustom
                style={{
                    ...uiFullWidth,
                }}
                type="Inactive"
            >
                <FlexBox
                    style={{
                        ...uiFullWidth,
                        marginBottom: ThemingParameters.sapContent_Space_Small,
                    }}
                    justifyContent={FlexBoxJustifyContent.SpaceBetween}
                    alignItems={FlexBoxAlignItems.Center}
                >
                    <Title data-testid="step-name" style={{ fontSize: ThemingParameters.sapFontHeader4Size }} level="H4">
                        {t(MonitorLayoutStep[name as keyof typeof MonitorLayoutStep])}
                    </Title>
                    <PersonalizationStatusIcon status={mapJobStatusToPersonalizationStatus(status)} />
                </FlexBox>
            </ListItemCustom>

            <hr
                style={{
                    width: '100%',
                    border: 'none',
                    borderTop: `1px solid ${ThemingParameters.sapGroup_TitleBorderColor}`,
                }}
            />

            <ListItemCustom
                style={{
                    marginBottom: ThemingParameters.sapContent_Space_Medium,
                    ...uiFullWidth,
                }}
                type="Inactive"
            >
                <FlexBox
                    style={{
                        marginTop: ThemingParameters.sapContent_Space_Small,
                        ...uiFullWidth,
                    }}
                    alignItems={FlexBoxAlignItems.Center}
                    justifyContent={FlexBoxJustifyContent.SpaceBetween}
                >
                    <Label
                        style={{
                            marginRight: ThemingParameters.sapContent_Space_Small,
                            fontSize: ThemingParameters.sapFontLargeSize,
                        }}
                    >
                        {t(mapUIStatus(status))}
                    </Label>

                    <Link
                        data-testid="activity-log-button"
                        style={{
                            height: 'fit-content',
                            fontSize: ThemingParameters.sapFontLargeSize,
                        }}
                        disabled={status === 'NOT_STARTED'}
                        onClick={() => {
                            const { close } = Modals.showDialog({
                                header: (
                                    <Bar
                                        data-testid="log-details"
                                        startContent={<Title level="H4">Log Details</Title>}
                                        endContent={<Icon data-testid="close-icon" name="decline" onClick={() => close()} />}
                                    />
                                ),
                                children: (
                                    <List separators="None">
                                        {activity?.map((activityContent, index: number) => (
                                            <NotificationListItem
                                                wrappingType="Normal"
                                                titleText={formatText(activityContent.description)}
                                                key={`${activityContent.description}-${index}`}
                                                footnotes={<Label>{formatDate(activityContent.date)}</Label>}
                                                style={{
                                                    marginBottom: ThemingParameters.sapContent_Space_Small,
                                                    borderBottom: `.25rem solid ${ThemingParameters.sapGroup_TitleBorderColor}`,
                                                }}
                                            />
                                        ))}
                                    </List>
                                ),
                            });
                        }}
                    >
                        {t('MORE_DETAILS_TEXT')}
                    </Link>
                </FlexBox>
            </ListItemCustom>
        </FlexBox>
    );
};

const styles = createStyles({
    contentWrapper: {
        flex: '1',
        marginLeft: ThemingParameters.sapContent_Margin_Small,
        marginRight: ThemingParameters.sapContent_Margin_Small,
    },
});

export default StepProgress;
